import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import { KeyRound } from "lucide-react";
import { useSession } from "../state/session";
import { Sheet } from "./Sheet";

/**
 * The Rung-1 moment: after the first reward, offer to save progress behind a
 * passkey (Better Auth passkey plugin). Dismissable, never nags again.
 */
export function PasskeyPrompt({ onDone }: { onDone: () => void }) {
  const t = useT();
  const { auth } = useSession();
  const queryClient = useQueryClient();
  const [state, setState] = useState<"idle" | "busy" | "done" | "failed">("idle");

  const add = async () => {
    setState("busy");
    try {
      const result = await auth.passkey.addPasskey();
      setState(result?.error ? "failed" : "done");
      // participant.me carries hasPasskey — refresh it so nothing offers a second one.
      if (!result?.error) void queryClient.invalidateQueries();
    } catch {
      setState("failed");
    }
  };

  return (
    <Sheet label={t("ec2.passkey.prompt.title")} onClose={onDone}>
      <div className="flex flex-col gap-3" data-testid="ec-passkey-prompt">
        <span className="flex size-10 items-center justify-center rounded-(--radius-chip) bg-primary/15 text-primary">
          <KeyRound size={20} aria-hidden />
        </span>
        <h2 className="font-display text-[21px] font-[650] text-fg">
          {t("ec2.passkey.prompt.title")}
        </h2>
        <p className="text-base text-fg-secondary">{t("ec2.passkey.prompt.body")}</p>
        {state === "done" && (
          <p role="status" className="text-[13px] font-medium text-success">
            {t("ec2.passkey.done")}
          </p>
        )}
        {state === "failed" && (
          <p role="alert" className="text-[13px] font-medium text-danger">
            {t("ec2.passkey.failed")}
          </p>
        )}
        <div className="mt-1 flex items-center justify-between gap-3">
          {state === "done" ? (
            <Button variant="primary" className="w-full" onClick={onDone}>
              {t("ec2.common.close")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onDone} data-testid="ec-passkey-later">
                {t("ec2.passkey.prompt.later")}
              </Button>
              <Button
                variant="primary"
                onClick={() => void add()}
                disabled={state === "busy"}
                data-testid="ec-passkey-add"
              >
                {t("ec2.passkey.prompt.cta")}
              </Button>
            </>
          )}
        </div>
      </div>
    </Sheet>
  );
}
